// src/pages/HomeScreen.jsx
import { useEffect, useState } from "react";
import { Routes, Route, NavLink, useLocation } from "react-router-dom";
import { ReactComponent as AvatarIcon } from "../assets/icons/avatar.svg";
import { isAdminDomain } from "../App.js";
import "../styles/colors.css";
import "../styles/common.css";
import "../styles/layout.css";
import { decodeJwt } from "../util/decodeJwt";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";

import CustomerManagement from "./CustomerManagemen";
import InventoryLogs from "./InventoryLogs";
import InventoryManagement from "./InventoryManagement";
import OrganizationManagement from "./OrganizationManagement";
import UserManagement from "./UserManagement";
import Inward from "../components/Inward";
import BackButton from "../components/BackButton";
import Outward from "../components/outward";
import ModelManagement from "./ModelManagement";
import OrganizationCreateOrUpdateWidget from "../components/OrganizationCreateOrUpdateWidget";
import UserCreateOrUpdateWidget from "../components/UserCreateOrUpdateWidget";
import CustomerCreateOrUpdateWidget from "../components/CustomerCreateOrUpdateWidget";
import ModelCreateOrUpdateWidget from "../components/ModelCreateOrUpdateWidget";
import Dashboard from "./InventoryOverviewDashboard";
import ProfileUpdateWidget from "../components/profile";
import MaintenanceOverviewDashboard from "./MaintenanceOverviewDashboard";
import InventoryOverview from "./InventoryOverviewDashboard";
import InventoryOverviewDashbboard from "./InventoryOverviewDashboard";
import ServiceSwap from "./ServiceSwap";
import DeviceSubscriptions from "./DeviceSubscriptions";

export default function HomeScreen({ onLogout }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [userRole, setUserRole] = useState("");
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userOrgType, setUserOrgType] = useState(""); //
  const [sidebarLogo, setSidebarLogo] = useState(localStorage.getItem("orgLogo") || "");
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
  };

  const handleProfile = () => {
    setIsUserMenuOpen(false);
    navigate(`/profile/${userId}`)
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  // Refresh sidebar logo whenever location changes (e.g., returning from profile page)
  useEffect(() => {
    setSidebarLogo(localStorage.getItem("orgLogo") || "");
  }, [location.pathname]);

  // Also refresh when App.js finishes fetching branding from DB (resolves race condition on login)
  useEffect(() => {
    const handleBrandingUpdate = () => {
      setSidebarLogo(localStorage.getItem("orgLogo") || "");
    };
    window.addEventListener("branding-updated", handleBrandingUpdate);
    return () => window.removeEventListener("branding-updated", handleBrandingUpdate);
  }, []);

  // Decode JWT to get user info
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decoded = decodeJwt(token);
      setUserName(decoded?.name || "Unknown User");
      setUserId(decoded._id || "")
      setUserRole(decoded?.role || "");
      setUserOrgType(decoded?.organizationType || decoded?.organization?.type || "");
    } else {
      setUserId("")
      setUserName("Unknown User");
    }
  }, []);

  // Menu items with role-based access
  const menuItems = [
    // { 
    //   name: "Inventory Overview", path: "/inventory-overview",
    //   roles: ["manufacturer","distributor","dealer"],
    // },
    {
      name: "Maintenance Overview", path: "/maintenance-overview",
      roles: ["manufacturer"],
    },
    {
      name: "Inventory Management",
      path: "/inventory-management",
      roles: ["admin", "manufacturer", "distributor", "dealer"],
    },
    {
      name: "Inventory Logs",
      path: "/inventory-logs",
      roles: ["admin", "manufacturer", "distributor", "dealer"],
    },
    // { name: "Users", path: "/users", roles: ["admin", "manufacturer","distributor"]},
    // { name: "Customers", path: "/customers", roles:[ "manufacturer","distributor","dealer"] },
    { name: "Service Swap", path: "/service-swap", roles: ["manufacturer"] },
    { name: "Subscription", path: "/subscription", roles: ["admin"] },

  ];

  // Filter menu based on user role
  const filteredMenu = menuItems.filter(
    (item) => !item.roles || item.roles.includes(userRole)
  );

  // Simple route protection wrapper
  const ProtectedRoute = ({ element, allowedRoles }) => {
    if (!allowedRoles.includes(userRole)) return <div>Access Denied</div>;
    return element;
  };

  return (
    <div className="layout">
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? "open" : ""}`}>
        {sidebarLogo && (
          <img
            src={sidebarLogo}
            alt="Brand Logo"
            className="logo-svg w-20 h-20"
          />
        )}
        <nav>
          {filteredMenu.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={() => setIsSidebarOpen(false)}
            >
              {item.name}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="main">
        <header className="header">
          <button
            className="hamburger"
            aria-label="Toggle menu"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>

          {/* Back button on routes other than home */}
          {(location.pathname !== "/") && (
            <BackButton className="header-back-button" />
          )}
          {userRole === "admin" ? (
            <h1>ADMIN PORTAL</h1>
          ) : (
            <h1>{localStorage.getItem("orgName") || "PORTAL"}</h1>
          )}

          <div className="user-info-wrapper" ref={userMenuRef}>
            <div className="user-info" onClick={toggleUserMenu}>
              <AvatarIcon className="avatar-icon" />
              <span>{userName}</span>
            </div>

            {isUserMenuOpen && (
              <div className="user-menu-dropdown">
                <button onClick={handleProfile}>Profile</button>
                <button onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <main className="body">
          <Routes>
            <Route path="/" element={<InventoryManagement />} />
            <Route path="/maintenance-overview" element={<MaintenanceOverviewDashboard />} />

            <Route path="/inventory-overview" element={<InventoryOverviewDashbboard />} />

            {/* Inventory */}
            <Route
              path="/inventory-management"
              element={
                <ProtectedRoute
                  element={<InventoryManagement />}
                  allowedRoles={["admin", "manufacturer", "distributor", "dealer"]}
                />
              }
            />
            <Route
              path="/inventory-management/inward"
              element={
                <ProtectedRoute
                  element={<Inward />}
                  allowedRoles={["admin", "manufacturer", "distributor", "dealer"]}
                />
              }
            />
            <Route
              path="/inventory-management/outward"
              element={
                <ProtectedRoute
                  element={<Outward />}
                  allowedRoles={["admin", "manufacturer", "distributor", "dealer"]}
                />
              }
            />

            {/* Users */}
            {/* <Route
              path="/users"
              element={
                <ProtectedRoute element={<UserManagement />} allowedRoles={["admin", "manufacturer","distributor"]} />
              }
            /> */}
            {/* <Route
              path="/users/create"
              element={
                <ProtectedRoute
                  element={<UserCreateOrUpdateWidget />}
                  allowedRoles={["admin", "manufacturer","distributor"]}
                />
              }
            /> */}
            {/* <Route
              path="/users/edit/:id"
              element={
                <ProtectedRoute
                  element={<UserCreateOrUpdateWidget />}
                  allowedRoles={["admin", "manufacturer","distributor"]}
                />
              }
            /> */}

            <Route
              path="/profile/:id"
              element={
                <ProtectedRoute
                  element={<ProfileUpdateWidget />}
                  allowedRoles={["admin", "manufacturer", "distributor", "dealer"]}
                />
              }
            />

            {/* Organizations */}
            <Route
              path="/organizations"
              element={
                <ProtectedRoute
                  element={<OrganizationManagement />}
                  allowedRoles={["admin", "manufacturer", "distributor"]}
                />
              }
            />
            <Route
              path="/organizations/create"
              element={
                <ProtectedRoute
                  element={<OrganizationCreateOrUpdateWidget />}
                  allowedRoles={["admin", "manufacturer", "distributor"]}
                />
              }
            />
            <Route
              path="/organizations/edit/:id"
              element={
                <ProtectedRoute
                  element={<OrganizationCreateOrUpdateWidget />}
                  allowedRoles={["admin", "manufacturer", "distributor"]}
                />
              }
            />

            {/* Customers */}
            <Route
              path="/customers"
              element={
                <ProtectedRoute
                  element={<CustomerManagement />}
                  allowedRoles={["manufacturer", "distributor", "dealer"]}
                />
              }
            />
            <Route
              path="/customers/create"
              element={
                <ProtectedRoute
                  element={<CustomerCreateOrUpdateWidget />}
                  allowedRoles={["manufacturer", "distributor", "dealer"]}
                />
              }
            />
            <Route
              path="/customers/edit/:id"
              element={
                <ProtectedRoute
                  element={<CustomerCreateOrUpdateWidget />}
                  allowedRoles={["manufacturer", "distributor", "dealer"]}
                />
              }
            />

            {/* Inventory Logs */}
            <Route
              path="/inventory-logs"
              element={
                <ProtectedRoute
                  element={<InventoryLogs />}
                  allowedRoles={["admin", "manufacturer", "distributor", "dealer"]}
                />
              }
            />


            {/* Service Swap */}
            <Route
              path="/service-swap"
              element={
                <ProtectedRoute
                  element={<ServiceSwap />}
                  allowedRoles={["manufacturer"]}
                />
              }
            />

            {/* Subscriptions */}
            <Route
              path="/subscription"
              element={
                <ProtectedRoute
                  element={<DeviceSubscriptions />}
                  allowedRoles={["admin"]}
                />
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}
